import { ReactNode, useEffect, useState } from 'react';
import Spinner from '@/components/Common/Spinner';
import { getLocation } from '@/apis/business/setting/location/api';
import { CacheKeys } from 'utils/cacheKeys';

const LOCATION_ROUTE_PATTERN = /\/location\/(\d+)/;

function getLocationIdFromPathname(pathname = window.location.pathname) {
  return pathname.match(LOCATION_ROUTE_PATTERN)?.[1];
}

type Props = {
  readonly children: ReactNode;
};

/**
 * On `/location/:id/...` deep links, resolve business/location into localStorage
 * before BusinessProvider reads persist keys (fixes stale Business-Id on cold load).
 */
export default function BusinessContextHydrator({ children }: Props) {
  const [ready, setReady] = useState(() => !getLocationIdFromPathname());

  useEffect(() => {
    const locationId = getLocationIdFromPathname();
    if (!locationId) return;

    let cancelled = false;

    getLocation(locationId)
      .then((location) => {
        if (cancelled || !location?.business_id || !location?.id) return;

        localStorage.setItem(CacheKeys.businessIds, JSON.stringify([location.business_id]));
        localStorage.setItem(CacheKeys.locationIds, JSON.stringify([location.id]));
      })
      .catch(() => {
        // Allow the app to mount even if location lookup fails.
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return <Spinner />;
  }

  return <>{children}</>;
}
